import store from '@/store';
import { getInstance } from '@/services/Auth';

let inFlight = null;

/**
 * Best-effort silent access-token renewal for the axios 401 interceptors.
 *
 * A 401 almost always means the short-lived access token expired while the
 * Auth0 SSO session (72h) is still valid. Rather than destroying that session
 * with a full logout + redirect — which forces the user back through login and
 * MFA — we mint a fresh token from the existing session and let the caller
 * retry. Concurrent 401s share a single renewal so a burst of expired requests
 * does not trigger a stampede of silent-auth calls.
 *
 * Rejects if renewal fails (session genuinely gone); callers fall back to
 * logout in that case.
 */
/**
 * Recover from a failed silent renewal WITHOUT destroying the Auth0 session.
 *
 * A failed renewal does not mean the session is dead. Silent auth runs in a
 * hidden iframe, so on tenants without a same-site custom domain the browser
 * blocks the session cookie in that third-party context and Auth0 returns
 * `login_required` even though the session is perfectly alive. Calling
 * `$auth.logout()` there would destroy a valid session and force a needless
 * full re-login (and MFA challenge).
 *
 * Redirecting to login is safe in both cases: a live session is recognised
 * first-party and returns silently, and a genuinely dead one logs in normally.
 * The intended route is preserved so the user lands back where they were.
 */
export const redirectToLogin = () => {
  const { pathname, search } = window.location;

  if (pathname !== '/' && pathname !== '/login' && !search.includes('code=')) {
    sessionStorage.setItem('authTargetUrl', pathname + search);
  }

  return getInstance().loginWithRedirect();
};

export default function renewToken() {
  if (!inFlight) {
    inFlight = getInstance()
      .getTokenSilently({ cacheMode: 'off' })
      .then(async (token) => {
        await store.dispatch('saveToken', { token, expiresIn: 86400 });
        return token;
      })
      .finally(() => {
        inFlight = null;
      });
  }

  return inFlight;
}
